import { TextField, Button, FormControl, Tabs, Tab, FormControlLabel } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';

import MyDialog from "~/mui/MyDialog";
import { checkForErrorsState } from '~/utils/utils';
import { useTrans } from '~/hooks/useTrans';
import { apiSendBulkEmail, apiSendTestEmail } from '~/providers/api';
import { myUseState } from "~/hooks/myUseState";
import { useLang } from "~/hooks/useLang";
import ScheduleAtInput from "~/mui/ScheduleAtInput";
import Wysiwyg, { contentToHTML, IWysiwygContent } from "~/components/Wysiwyg";
import { notificationStore } from "~/stores/notification";
import EmailPreviewModal from "../EmailPreviewModal";
import { MySwitch } from "~/mui/MySwitch";

import "./style.less";

const TABS = {
	SETTINGS: 0,
	CZECH: 1,
	ENGLISH: 2,
};
const MAX_BODY_LEN = 4000;

interface IAdminBulkEmailData {
	subjectCs: string;
	subjectEn: string;
	bodyCs: null | IWysiwygContent;
	bodyEn: null | IWysiwygContent;
	scheduleAt: string;
	testEmail: string;
}

interface IAdminBulkEmailErrorData extends Omit<IAdminBulkEmailData, "bodyCs" | "bodyEn"> {
	bodyCs: string;
	bodyEn: string;
}

interface IState {
	dialogLoading: boolean;
	errorMsg: string;
	wasChanged: boolean;
	sendAble: boolean;
	internalName: string;
	setInternalName: boolean;
	showContent: IWysiwygContent;
	showConfirm: boolean;
	tabInd: number;
	englishCzech: boolean;
	saveAsAdminMessage: boolean;
}

interface IAdminSendBulkEmailModal {
	projectIds: Array<number>;
	projectName?: string;
	onClose?: () => void;
	onSave?: () => void;
	onChange?: (isChanged: boolean) => void;
}

export default function AdminSendBulkEmailModal({
	projectIds,
	projectName = "",
	onClose = () => {},
	onSave = () => {},
	onChange = () => {},
}: IAdminSendBulkEmailModal) {
	const { trans } = useTrans();
	const { lang, isCzech } = useLang();
	const { state, updateState } = myUseState<IState>({
		dialogLoading: false,
		errorMsg: "",
		wasChanged: false,
		sendAble: false,
		internalName: "",
		setInternalName: false,
		showContent: null,
		showConfirm: false,
		tabInd: TABS.SETTINGS,
		englishCzech: false,
		saveAsAdminMessage: false,
	});
	const { state: stateFormData, updateState: updateFormData } = myUseState<IAdminBulkEmailData>({
		subjectCs: "",
		subjectEn: "",
		bodyCs: null,
		bodyEn: null,
		scheduleAt: "",
		testEmail: "",
	});
	const { state: stateFormError, setState: setFormError } = myUseState<IAdminBulkEmailErrorData>({
		subjectCs: "",
		subjectEn: "",
		bodyCs: "",
		bodyEn: "",
		scheduleAt: "",
		testEmail: "",
	});
	const { setNotification } = notificationStore(notificationState => ({
		setNotification: notificationState.setNotification,
	}));

	function getUpdatedData(newState: Partial<IAdminBulkEmailData>, englishCzech = false): Partial<IState> {
		const newStateObj = {
			...stateFormData,
			...newState,
		};
		const hasSubtitleCs = newStateObj.subjectCs !== "";
		const hasSubtitleEn = newStateObj.subjectEn !== "";
		const hasBodyCs = newStateObj.bodyCs && newStateObj.bodyCs.getPlainText() !== "";
		const hasBodyEn = newStateObj.bodyEn && newStateObj.bodyEn.getPlainText() !== "";
		const sendAble = hasSubtitleCs && hasBodyCs && (englishCzech || (hasSubtitleEn && hasBodyEn));
		const wasChanged = hasSubtitleCs || hasSubtitleEn || hasBodyCs || hasBodyEn
			|| newStateObj.scheduleAt !== "";

		return {
			sendAble,
			wasChanged,
			englishCzech,
		};
	}

	function updateDataWithChange(newState: Partial<IAdminBulkEmailData>) {
		const changedData = getUpdatedData(newState, state.englishCzech);

		updateFormData(newState);
		updateState({
			...changedData,
		});
		onChange(changedData.wasChanged);
	}

	function updateScheduleAt(date: Date | null) {
		updateDataWithChange({
			scheduleAt: date
				? date.toISOString()
				: "",
		});
	}

	function updateOptionWithChange(changes: Partial<IState>) {
		const newState = {
			...state,
			...changes,
		};
		const changedData = getUpdatedData(stateFormData, newState.englishCzech);

		updateState({
			...changes,
			...changedData,
		});
	}

	function checkFormData(checkTestEmail?: boolean) {
		const formData: IAdminBulkEmailErrorData = {
			...stateFormData,
			bodyCs: stateFormData.bodyCs?.getPlainText() || "",
			bodyEn: stateFormData.bodyEn?.getPlainText() || "",
		};
		const errorData = checkForErrorsState<IAdminBulkEmailErrorData, IAdminBulkEmailErrorData>(formData, {
			subjectCs: { check: "string-5-50", msg: trans("bulkEmailModal.error.subject") },
			bodyCs: { check: "string-gt5", msg: trans("bulkEmailModal.error.emptyBody") },
			...state.englishCzech
				? {}
				: {
					subjectEn: { check: "string-5-50", msg: trans("bulkEmailModal.error.subject") },
					bodyEn: { check: "string-gt5", msg: trans("bulkEmailModal.error.emptyBody") },
				},
			...checkTestEmail
				? {
					testEmail: { check: "email", msg: trans("userRole.error.email") },
				}
				: {},
		});
		const additionalErrors: Partial<IAdminBulkEmailErrorData> = {};
		let errorsCount = errorData.errorsCount;

		if (stateFormData.bodyCs) {
			const csHtml = contentToHTML(stateFormData.bodyCs);
			const csLen = csHtml.length;

			if (csLen > MAX_BODY_LEN) {
				additionalErrors.bodyCs = trans("bulkEmailModal.error.bodyMaxLen", {
					curCount: csLen,
					maxCount: MAX_BODY_LEN,
				});
				errorsCount++;
			}
		}

		if (stateFormData.bodyEn) {
			const enHtml = contentToHTML(stateFormData.bodyEn);
			const enLen = enHtml.length;

			if (enHtml.length > MAX_BODY_LEN) {
				additionalErrors.bodyEn = trans("bulkEmailModal.error.bodyMaxLen", {
					curCount: enLen,
					maxCount: MAX_BODY_LEN,
				});
				errorsCount++;
			}
		}

		setFormError({
			testEmail: "",
			...errorData.errorsState,
			...additionalErrors,
		});

		if (errorsCount > 0) {
			return false;
		}

		return true;
	}

	async function sendTestEmail() {
		if (!checkFormData(true)) {
			return;
		}

		updateState({
			dialogLoading: true,
			errorMsg: "",
		});

		const outputData = await apiSendTestEmail({
			email: stateFormData.testEmail,
			subject: {
				cs: stateFormData.subjectCs,
				en: state.englishCzech ? stateFormData.subjectCs : stateFormData.subjectEn,
			},
			body: {
				cs: contentToHTML(stateFormData.bodyCs),
				en: contentToHTML(state.englishCzech ? stateFormData.bodyCs : stateFormData.bodyEn),
			},
			saveAsAdminMessage: state.saveAsAdminMessage,
		}, lang);

		if (outputData.noErrors) {
			updateState({
				dialogLoading: false,
			});
			setNotification(trans("bulkEmailModal.testEmail.sended"));
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	async function dialogOnSave() {
		updateState({
			showConfirm: false,
			dialogLoading: true,
			errorMsg: "",
		});

		const outputData = await apiSendBulkEmail({
			projectIds,
			subject: {
				cs: stateFormData.subjectCs,
				en: state.englishCzech ? stateFormData.subjectCs : stateFormData.subjectEn,
			},
			body: {
				cs: contentToHTML(stateFormData.bodyCs),
				en: contentToHTML(state.englishCzech ? stateFormData.bodyCs : stateFormData.bodyEn),
			},
			scheduleAt: stateFormData.scheduleAt
				// python zhavaruje, pokud jen na konci Z .replace("Z", "") TODO
				? stateFormData.scheduleAt
				: null,
			saveAsAdminMessage: state.saveAsAdminMessage,
		}, lang);

		if (outputData.noErrors) {
			updateState({
				dialogLoading: false,
			});
			setNotification(trans("bulkEmailModal.bulkEmail.sended"));
			onSave();
		} else {
			updateState({
				dialogLoading: false,
				errorMsg: outputData.errors.join(", "),
			});
		}
	}

	function onConfirmSave() {
		if (!checkFormData()) {
			return;
		}

		updateState({
			showConfirm: true,
		});
	}

	return <MyDialog title={trans("bulkEmailModal.title")} onClose={onClose} onSave={onConfirmSave} contentClassName="adminSendBulkEmailModal" dialogLoading={state.dialogLoading} disableSave={!state.sendAble}
		errorMsg={state.errorMsg} saveCaption={trans("bulkEmailModal.send")} cancelCaption={trans("close")} confirmClose={state.wasChanged} fullScreen={true} contentSx={{ maxWidth: "100%" }}>
		<Tabs value={ state.tabInd } onChange={(___, tabInd) => updateState({ tabInd })}>
			<Tab label={trans("bulkEmailModal.tab1")} />
			<Tab label={trans("bulkEmailModal.tab2")} icon={(stateFormError.subjectCs || stateFormError.bodyCs) && <ErrorIcon />} iconPosition="end" />
			<Tab label={trans("bulkEmailModal.tab3")} icon={(stateFormError.subjectEn || stateFormError.bodyEn) && <ErrorIcon />} iconPosition="end" />
		</Tabs>
		{ state.tabInd === TABS.SETTINGS && <div className="adminSendBulkEmailModal__tabContent">
			<p className="adminSendBulkEmailModal__info">
				{ trans("bulkEmailModal.infoLine1") }&nbsp;
				<strong>
					{ trans("bulkEmailModal.infoLine2", { count: projectIds.length }) }
					{ projectName && ` - ${projectName}` }
				</strong>
			</p>
			<ScheduleAtInput value={stateFormData.scheduleAt} isCzech={isCzech} onChange={updateScheduleAt} sx={{ marginBottom: "var(--margin-large)" }} />
			<TextField
				label={trans("bulkEmailModal.testEmail")}
				value={stateFormData.testEmail}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ testEmail: event.target.value })}
				color="success"
				autoComplete="off"
				error={stateFormError.testEmail !== ""}
				helperText={stateFormError.testEmail}
				size="small"
			/>
			{ projectIds.length === 1 && <div className="margin-top-large">
				<FormControlLabel
					className="adminSendBulkEmailModal__englishCzech"
					control={<MySwitch sx={{ m: 1 }} checked={state.englishCzech} onChange={event => updateOptionWithChange({ englishCzech: event.target.checked })} />}
					label={trans("bulkEmailModal.optionEnglishCzech")}
				/>
				<FormControlLabel
					className="adminSendBulkEmailModal__saveAsAdminMessage"
					control={<MySwitch sx={{ m: 1 }} checked={state.saveAsAdminMessage} onChange={event => updateOptionWithChange({ saveAsAdminMessage: event.target.checked })} />}
					label={trans("bulkEmailModal.optionInsertEmail")}
				/>
			</div> }
			<div className="adminSendBulkEmailModal__btnsArea">
				<FormControl size="small">
					<Button color="success" variant="contained" onClick={sendTestEmail} disabled={!state.sendAble || stateFormData.testEmail === ""}>
						{ trans("bulkEmailModal.sendTest") }
					</Button>
				</FormControl>
			</div>
		</div>}
		{ state.tabInd === TABS.CZECH && <div className="adminSendBulkEmailModal__tabContent">
			<TextField
				label={trans("bulkEmailModal.subjectCs")}
				value={stateFormData.subjectCs}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ subjectCs: event.target.value })}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.subjectCs !== ""}
				helperText={stateFormError.subjectCs}
				size="small"
			/>
			<Wysiwyg title={trans("bulkEmailModal.bodyCs")} content={stateFormData.bodyCs} error={stateFormError.bodyCs} onChange={content => updateDataWithChange({ bodyCs: content })} />
			<div className="adminSendBulkEmailModal__btnsArea">
				<FormControl size="small">
					<Button color="success" variant="contained" onClick={() => updateState({ showContent: stateFormData.bodyCs })} disabled={!stateFormData.bodyCs}>
						{ trans("bulkEmailModal.preview") }
					</Button>
				</FormControl>
			</div>
		</div>}
		{ state.tabInd === TABS.ENGLISH && <div className="adminSendBulkEmailModal__tabContent">
			<TextField
				label={trans("bulkEmailModal.subjectEn")}
				value={stateFormData.subjectEn}
				style={{ width: "100%" }}
				onChange={event => updateDataWithChange({ subjectEn: event.target.value })}
				color="success"
				autoComplete="off"
				sx={{ marginBottom: "var(--margin-large)" }}
				error={stateFormError.subjectEn !== ""}
				helperText={stateFormError.subjectEn}
				size="small"
			/>
			<Wysiwyg title={trans("bulkEmailModal.bodyEn")} content={stateFormData.bodyEn} error={stateFormError.bodyEn} onChange={content => updateDataWithChange({ bodyEn: content })} />
			<div className="adminSendBulkEmailModal__btnsArea">
				<FormControl size="small">
					<Button color="success" variant="contained" onClick={() => updateState({ showContent: stateFormData.bodyEn })} disabled={!stateFormData.bodyEn}>
						{ trans("bulkEmailModal.preview") }
					</Button>
				</FormControl>
			</div>
		</div>}
		{ state.showContent && <EmailPreviewModal content={state.showContent} onClose={() => updateState({ showContent: null })} /> }
		{ state.showConfirm && <MyDialog title={trans("bulkEmailModal.title")} onSave={dialogOnSave} onClose={() => updateState({ showConfirm: false })} saveCaption={trans("bulkEmailModal.confirmBtn")}>
			<p>
				{ trans("bulkEmailModal.infoLine1") }&nbsp;
				<strong>
					{ trans("bulkEmailModal.infoLine2", { count: projectIds.length }) }
					{ projectName && ` - ${projectName}` }
				</strong><br />
				{ trans("bulkEmailModal.confirm") }
			</p>
		</MyDialog> }
	</MyDialog>;
}
